<script setup>
/**
 * "Write it for me" (spec P-8.1): a first draft of the Personality, a few answers and the questions
 * only she can answer, from what Waslo already knows about the business.
 *
 * ── THREE SCREENS, ONE PAGE, NOTHING TELEPORTS ───────────────────────────────────────────
 *   found   what Waslo found: the WhatsApp profile of each number, the account's name, a website
 *           box. "Read the website first" is its own priced step (the existing source import),
 *           and the draft waits for it; "Skip the website" drafts without it.
 *   review  every drafted line with its source, Keep / Edit / Remove; the questions.
 *   saved   one Undo for the whole save.
 *
 * A PAID DRAFT IS NEVER BOUGHT TWICE. It is stored as a proposal, so a reload opens the same draft
 * (the newest open one) and charges nothing. Save sends only her choices (`{ keep, edits }`); the
 * server composes the Personality (F26).
 *
 * Every AI call here is priced and confirmed first (P-5.12): reading the website (`add_source`),
 * the draft (`first_draft`), and Save these when it adds answers (`apply_answers`). The confirm is
 * drawn in place.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import AiConfirmInline from '../components/AiConfirmInline.vue';
import FirstDraftReview from '../components/FirstDraftReview.vue';
import WasloInput from '../components/WasloInput.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { useJobPoller } from '../composables/useJobPoller.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useWasloI18n } from '../i18n.js';
import { businessProfileApi } from '../api/business-profile.js';
import { wasloApi, withConfirm } from '../api/client.js';
import {
  answersAddedBy,
  initialDecisions,
  keepBodyOf,
} from '../helpers/setup-ai.js';

const { t, locale } = useWasloI18n();
const router = useRouter();
const accountId = useWasloAccountId();

const draftPrice = useAiConfirm({ accountId, op: 'first_draft' });
const websitePrice = useAiConfirm({ accountId, op: 'add_source' });
const savePrice = useAiConfirm({ accountId, op: 'apply_answers' });

const loading = ref(true);
const loadError = ref('');
const isDefault = ref(true);
const name = ref('');
const profiles = ref([]);
const website = ref('');

/** `found` | `reading` | `review` | `saved` */
const stage = ref('found');
const busy = ref(false);
const failure = ref('');
const documentId = ref(null);
const jobId = ref(null);

const suggestion = ref(null);
const decisions = ref({});
const answered = ref({});
const saved = ref(null);
const undone = ref(false);

const lang = computed(() =>
  String(locale.value ?? '').startsWith('ar') ? 'ar' : 'en'
);
const personalityLink = computed(() =>
  accountId.value === null
    ? ''
    : frontendURL(`accounts/${accountId.value}/waslo/personality`)
);

const open = found => {
  suggestion.value = found;
  decisions.value = initialDecisions(found.draft);
  answered.value = {};
  stage.value = 'review';
};

const load = async () => {
  loading.value = true;
  loadError.value = '';
  try {
    const [config, drafts, found, account] = await Promise.all([
      wasloApi.getConfig(accountId.value),
      wasloApi.getSetupSuggestions(accountId.value, 'first_draft'),
      businessProfileApi.profiles(accountId.value),
      businessProfileApi.accountName(accountId.value),
    ]);
    isDefault.value = config.personaIsDefault === true;
    profiles.value = found;
    name.value = account;
    website.value = found.flatMap(p => p.websites)[0] ?? '';
    // A draft already paid for is the one she sees: never a second charge for the same work.
    if (drafts.length > 0 && drafts[0].draft !== null) open(drafts[0]);
  } catch {
    loadError.value = t('WASLO.COMMON.ERROR_BODY');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  if (accountId.value !== null) load();
});
watch(accountId, value => {
  if (value !== null) load();
});

// ── the website, read first through the ordinary source import ──────────────────────
const poller = useJobPoller(async () => {
  const jobs = await wasloApi.getJobs(accountId.value);
  const job = jobs.find(row => row.id === jobId.value);
  if (!job || job.status === 'queued' || job.status === 'running') return;
  poller.stop();
  if (job.status === 'succeeded' && job.documentId !== null) {
    documentId.value = job.documentId;
    stage.value = 'found';
  } else {
    failure.value = t('WASLO.SETUP_AI.FIRST_DRAFT.WEBSITE_FAILED');
    stage.value = 'found';
  }
});

const readWebsite = async () => {
  const url = website.value.trim();
  if (url === '' || busy.value) return;
  busy.value = true;
  failure.value = '';
  try {
    const outcome = await websitePrice.confirmAndRun({
      run: credits =>
        wasloApi.addDocument(
          accountId.value,
          { type: 'url', title: url, url },
          undefined,
          withConfirm(credits)
        ),
    });
    if (outcome.cancelled) return;
    jobId.value = outcome.result.id;
    stage.value = 'reading';
    poller.start();
  } catch (error) {
    failure.value =
      error?.message || t('WASLO.SETUP_AI.FIRST_DRAFT.WEBSITE_FAILED');
  } finally {
    busy.value = false;
  }
};

// ── the draft ────────────────────────────────────────────────────────────────────────
const draftLabel = computed(() =>
  busy.value && stage.value === 'found'
    ? t('WASLO.SETUP_AI.FIRST_DRAFT.DRAFTING')
    : draftPrice.label(t('WASLO.SETUP_AI.FIRST_DRAFT.DRAFT'))
);

const draft = async () => {
  if (busy.value) return;
  busy.value = true;
  failure.value = '';
  try {
    const outcome = await draftPrice.confirmAndRun({
      run: credits =>
        wasloApi.firstDraft(
          accountId.value,
          {
            accountName: name.value,
            profiles: profiles.value.map(p => ({
              inboxId: p.inboxId,
              about: p.about,
              description: p.description,
              address: p.address,
              email: p.email,
              vertical: p.vertical,
              websites: p.websites,
            })),
            ...(documentId.value === null
              ? {}
              : { documentId: documentId.value }),
            lang: lang.value,
          },
          undefined,
          withConfirm(credits)
        ),
    });
    if (outcome.cancelled) return;
    open(outcome.result.suggestion);
  } catch (error) {
    failure.value =
      error?.status === 422
        ? t('WASLO.SETUP_AI.FIRST_DRAFT.UNUSABLE')
        : error?.message || t('WASLO.SETUP_AI.FIRST_DRAFT.FAILED');
  } finally {
    busy.value = false;
  }
};

const skipWebsite = () => {
  website.value = '';
  draft();
};

// ── saving what she kept ─────────────────────────────────────────────────────────────
const body = computed(() =>
  suggestion.value === null
    ? { keep: [], edits: {} }
    : keepBodyOf(suggestion.value.draft, decisions.value, answered.value)
);
const addsAnswers = computed(
  () =>
    suggestion.value !== null &&
    answersAddedBy(suggestion.value.draft, body.value) > 0
);
const nothingKept = computed(
  () =>
    body.value.keep.length === 0 &&
    Object.keys(body.value.edits.questions ?? {}).length === 0
);
const saveLabel = computed(() =>
  addsAnswers.value
    ? savePrice.label(t('WASLO.SETUP_AI.FIRST_DRAFT.SAVE'))
    : t('WASLO.SETUP_AI.FIRST_DRAFT.SAVE')
);

const save = async () => {
  if (suggestion.value === null || busy.value || nothingKept.value) return;
  busy.value = true;
  failure.value = '';
  const id = suggestion.value.id;
  try {
    let result;
    if (addsAnswers.value) {
      const outcome = await savePrice.confirmAndRun({
        run: credits =>
          wasloApi.applySetupSuggestion(
            accountId.value,
            id,
            body.value,
            undefined,
            withConfirm(credits)
          ),
      });
      if (outcome.cancelled) return;
      result = outcome.result;
    } else {
      // Nothing here is AI: no price, no dialog, no header.
      result = await wasloApi.applySetupSuggestion(
        accountId.value,
        id,
        body.value
      );
    }
    saved.value = result;
    stage.value = 'saved';
  } catch (error) {
    failure.value = error?.message || t('WASLO.SETUP_AI.FIRST_DRAFT.FAILED');
  } finally {
    busy.value = false;
  }
};

const startAgain = async () => {
  if (suggestion.value === null || busy.value) return;
  busy.value = true;
  try {
    await wasloApi.dismissSetupSuggestion(accountId.value, suggestion.value.id);
    suggestion.value = null;
    stage.value = 'found';
  } catch (error) {
    failure.value = error?.message || t('WASLO.SETUP_AI.FIRST_DRAFT.FAILED');
  } finally {
    busy.value = false;
  }
};

const undo = async () => {
  if (saved.value === null || busy.value) return;
  busy.value = true;
  try {
    await wasloApi.undoBatch(accountId.value, saved.value.batchId);
    undone.value = true;
  } catch (error) {
    failure.value = error?.message || t('WASLO.SETUP_AI.FIRST_DRAFT.FAILED');
  } finally {
    busy.value = false;
  }
};

const backToPersonality = () => {
  if (personalityLink.value) router.push(personalityLink.value);
};
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.SETUP_AI.FIRST_DRAFT.TITLE')"
    :back-url="personalityLink"
    :back-label="t('WASLO.PERSONALITY.TITLE')"
    :is-fetching="loading"
    :error-message="loadError"
    @retry="load"
  >
    <template #body>
      <div
        class="flex flex-col gap-8 pb-8"
        data-waslo-first-draft
        :data-stage="stage"
      >
        <p
          v-if="!isDefault && stage !== 'saved' && suggestion === null"
          class="mb-0 text-sm text-n-slate-12"
          data-waslo-first-draft-written
        >
          {{ t('WASLO.SETUP_AI.FIRST_DRAFT.ALREADY_WRITTEN') }}
        </p>

        <template v-else-if="stage === 'found' || stage === 'reading'">
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('WASLO.SETUP_AI.FIRST_DRAFT.FOUND_INTRO') }}
          </p>
          <ul
            class="flex flex-col gap-3 mb-0 list-none"
            data-waslo-first-draft-found
          >
            <li
              v-if="name"
              class="text-sm text-n-slate-12"
              data-waslo-first-draft-account
            >
              {{ t('WASLO.SETUP_AI.FIRST_DRAFT.ACCOUNT_NAME', { name }) }}
            </li>
            <li
              v-for="profile in profiles"
              :key="profile.inboxId"
              class="flex flex-col gap-1 text-sm"
              :data-waslo-first-draft-profile="profile.inboxId"
            >
              <span class="font-medium text-n-slate-12">
                <bdi>{{ profile.name || profile.phoneNumber }}</bdi>
              </span>
              <span v-if="profile.about" class="text-n-slate-11">
                <bdi>{{ profile.about }}</bdi>
              </span>
              <span v-if="profile.description" class="text-n-slate-11">
                <bdi>{{ profile.description }}</bdi>
              </span>
              <span v-if="profile.address" class="text-n-slate-11">
                <bdi>{{ profile.address }}</bdi>
              </span>
            </li>
            <li
              v-if="!name && profiles.length === 0"
              class="text-sm text-n-amber-11"
              data-waslo-first-draft-nothing
            >
              {{ t('WASLO.SETUP_AI.FIRST_DRAFT.NOTHING_FOUND') }}
            </li>
          </ul>

          <WasloInput
            v-model="website"
            :label="t('WASLO.SETUP_AI.FIRST_DRAFT.WEBSITE')"
            :hint="t('WASLO.SETUP_AI.FIRST_DRAFT.WEBSITE_HINT')"
            placeholder="https://"
            :disabled="busy || stage === 'reading' || documentId !== null"
            data-waslo-first-draft-website
          />
          <p
            v-if="stage === 'reading'"
            class="mb-0 text-sm text-n-slate-11"
            data-waslo-first-draft-reading
          >
            {{ t('WASLO.SETUP_AI.FIRST_DRAFT.READING') }}
          </p>
          <p
            v-if="documentId !== null"
            class="mb-0 text-sm text-n-teal-11"
            data-waslo-first-draft-website-read
          >
            {{ t('WASLO.SETUP_AI.FIRST_DRAFT.WEBSITE_READ') }}
          </p>

          <div class="flex flex-wrap gap-2">
            <template v-if="website.trim() !== '' && documentId === null">
              <Button
                :label="
                  websitePrice.label(
                    t('WASLO.SETUP_AI.FIRST_DRAFT.READ_WEBSITE')
                  )
                "
                :disabled="busy || stage === 'reading'"
                data-waslo-first-draft-read-website
                @click="readWebsite"
              />
              <Button
                :label="t('WASLO.SETUP_AI.FIRST_DRAFT.SKIP_WEBSITE')"
                variant="faded"
                color="slate"
                :disabled="busy"
                data-waslo-first-draft-skip-website
                @click="skipWebsite"
              />
            </template>
            <Button
              v-else
              :label="draftLabel"
              :disabled="busy || stage === 'reading'"
              data-waslo-first-draft-draft
              @click="draft"
            />
          </div>
          <AiConfirmInline :controller="websitePrice" />
          <AiConfirmInline :controller="draftPrice" />
        </template>

        <template v-else-if="stage === 'review' && suggestion !== null">
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('WASLO.SETUP_AI.FIRST_DRAFT.REVIEW_INTRO') }}
          </p>
          <FirstDraftReview
            v-model:decisions="decisions"
            v-model:answered="answered"
            :draft="suggestion.draft"
            :disabled="busy"
          />
          <p
            v-if="addsAnswers"
            class="mb-0 text-sm text-n-slate-11"
            data-waslo-first-draft-teach-note
          >
            {{ t('WASLO.SETUP_AI.FIRST_DRAFT.TEACH_NOTE') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <Button
              :label="saveLabel"
              :disabled="busy || nothingKept"
              data-waslo-first-draft-save
              @click="save"
            />
            <Button
              :label="t('WASLO.SETUP_AI.FIRST_DRAFT.START_AGAIN')"
              variant="faded"
              color="slate"
              :disabled="busy"
              data-waslo-first-draft-start-again
              @click="startAgain"
            />
          </div>
          <AiConfirmInline :controller="savePrice" />
        </template>

        <template v-else-if="stage === 'saved'">
          <p class="mb-0 text-sm text-n-slate-12" data-waslo-first-draft-saved>
            {{
              undone
                ? t('WASLO.SETUP_AI.FIRST_DRAFT.UNDONE')
                : t('WASLO.SETUP_AI.FIRST_DRAFT.SAVED')
            }}
          </p>
          <div class="flex flex-wrap gap-2">
            <Button
              :label="t('WASLO.SETUP_AI.FIRST_DRAFT.OPEN_PERSONALITY')"
              data-waslo-first-draft-open
              @click="backToPersonality"
            />
            <Button
              v-if="!undone"
              :label="t('WASLO.SETUP_AI.UNDO')"
              variant="faded"
              color="slate"
              :disabled="busy"
              data-waslo-first-draft-undo
              @click="undo"
            />
          </div>
        </template>

        <p
          v-if="failure"
          class="mb-0 text-sm text-n-ruby-11"
          data-waslo-first-draft-failure
        >
          {{ failure }}
        </p>
      </div>
    </template>
  </WasloPageLayout>
</template>
